<template>
  <div class="coach-note-page">
    <PatientHeader :patient="patientInfo" :refresh-handler="refreshAll" class="mb-4" />

    <!-- Modal chọn Patient ID -->
    <b-modal
      v-model="showPatientSelectModal"
      title="Chọn hồ sơ bệnh nhân"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      centered
      @ok="confirmSelectPatientId"
      @cancel="cancelSelectPatientId"
      ok-title="Xác nhận"
      ok-variant="success"
      cancel-title="Huỷ"
    >
      <p class="mb-3">Số điện thoại có nhiều hồ sơ. Vui lòng chọn hồ sơ cần xem:</p>
      <b-form-group>
        <b-form-radio
          v-for="opt in patientOptions"
          :key="opt.key"
          v-model="pendingPatientKey"
          :value="opt.key"
          class="mb-3"
        >
          <div class="font-weight-bold">{{ opt.title }}</div>
          <div v-if="opt.details.length" class="text-muted small">{{ opt.details.join(' · ') }}</div>
        </b-form-radio>
      </b-form-group>
    </b-modal>

    <div class="cn-layout">
      <!-- Main content -->
      <div class="cn-main">
        <b-tabs v-model="activeTab" class="cn-tabs">
          <b-tab title="Tuần hiện tại">
            <ProgramStatusBar
              :user-package-id="userPackageId"
              :is-update-permission="isUpdatePermission"
            />
            <Tab1WeekCurrent
              ref="tab1"
              :user-package-id="userPackageId"
              :week-number="selectedWeek"
              :week-list="weekList"
              :week-list-ready="weekListInitialized"
              :external-id="selectedExternalId"
              :patient-id="selectedPatientId"
              :is-view-permission="isViewPermission"
              :is-insert-permission="isInsertPermission"
              :is-update-permission="isUpdatePermission"
              :is-delete-permission="isDeletePermission"
              @change-week="onWeekSelect"
              @week-completed="loadWeekList"
            />
          </b-tab>
          <b-tab title="Tiến độ &amp; Lịch sử">
            <Tab2Progress :user-package-id="userPackageId" />
          </b-tab>
          <b-tab title="Tổng trạng &amp; Mục tiêu">
            <Tab3BaselineGoal
              :user-package-id="userPackageId"
              :his-baseline-data="hisBaselineData"
              :package-start-date="packageStartDate"
              :is-view-permission="isViewPermission"
              :is-insert-permission="isInsertPermission"
              :is-update-permission="isUpdatePermission"
              :is-delete-permission="isDeletePermission"
            />
          </b-tab>
        </b-tabs>
      </div>
      <div v-if="activeTab === 0" class="cn-sidebar">
        <ProgressTracker
          :week-list="weekList"
          :selected-week="selectedWeek"
          @select="onWeekSelect"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Tab1WeekCurrent from './components/Tab1WeekCurrent.vue';
import Tab2Progress from './components/Tab2Progress.vue';
import Tab3BaselineGoal from './components/Tab3BaselineGoal.vue';
import ProgressTracker from './components/ProgressTracker.vue';
import PatientHeader from './components/PatientHeader.vue';
import ProgramStatusBar from './components/ProgramStatusBar.vue';
import { getWeekList, getUserPackage, getCustomerInfo } from './services/services.js';
import {
  getLatestCheckupByPhoneBeforeDate,
  getPatientIdsByPhone,
  getPatientsByPhone,
} from '@/core/services/his.api.service';
import { latestVisit, prescriptionsOfVisit, doctorAndNoteOf } from './services/his-data';
import { hasPermission } from '@/core/services/permission.service';
import { PAGE_CODE, PERMISSION_TYPE } from '@/core/plugins/constants';

export default {
  name: 'CoachNoteDetail',
  components: {
    Tab1WeekCurrent,
    Tab2Progress,
    Tab3BaselineGoal,
    ProgressTracker,
    PatientHeader,
    ProgramStatusBar,
  },
  props: {
    userPackageId: {
      type: String,
      default: null,
    },
    phoneNumber: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedWeek: 1,
      activeTab: 0,
      weekList: [],
      weekListInitialized: false,
      hisBaselineData: null,
      selectedExternalId: null,
      // patientId HIS của hồ sơ đã chọn (ưu tiên hơn externalId khi gọi aggregate)
      selectedPatientId: null,
      // [{ key, externalId, patientId, title, details }]
      patientOptions: [],
      showPatientSelectModal: false,
      pendingPatientKey: null,
      hisLoadSeq: 0,
      patientInfo: {
        name: '',
        phone: '',
        birthYear: '',
        address: '',
        program: 'Quản lý đường huyết',
        doctor: '',
        healthCoach: 'Nguyễn Thị C',
        diagnosis: '',
      },
    };
  },
  computed: {
    packageStartDate() {
      return this.weekList.find(w => w.weekNumber === 0)?.weekStartDate ?? null;
    },
    isViewPermission() {
      return hasPermission(PAGE_CODE.COACH_NOTE, PERMISSION_TYPE.VIEW);
    },
    isInsertPermission() {
      return hasPermission(PAGE_CODE.COACH_NOTE, PERMISSION_TYPE.INSERT);
    },
    isUpdatePermission() {
      return hasPermission(PAGE_CODE.COACH_NOTE, PERMISSION_TYPE.UPDATE);
    },
    isDeletePermission() {
      return hasPermission(PAGE_CODE.COACH_NOTE, PERMISSION_TYPE.DELETE);
    },
  },
  mounted() {
    this.loadWeekList();
    this.loadUserPackage();
    this.loadExternalId();
  },
  methods: {
    async loadCustomerInfoFallback() {
      if (!this.userPackageId) return;
      try {
        const res = await getCustomerInfo(this.userPackageId);
        const info = res?.data ?? res;
        if (!info) return;
        this.patientInfo = {
          ...this.patientInfo,
          name: info.name || this.patientInfo.name,
          phone: info.phoneNumber || this.phoneNumber,
          birthYear: info.birthYear || this.patientInfo.birthYear,
          program: info.servicePackageName || this.patientInfo.program,
        };
      } catch (_) {}
    },
    async loadUserPackage() {
      if (!this.userPackageId) return;
      try {
        const res = await getUserPackage(this.userPackageId);
        const pkg = res?.data ?? res;
        if (pkg) {
          this.patientInfo = {
            ...this.patientInfo,
            program: pkg.tranServicePackageName || this.patientInfo.program,
            healthCoach: pkg.coachName || this.patientInfo.healthCoach,
          };
        }
      } catch (_) {}
    },
    async loadWeekList() {
      try {
        const res = await getWeekList(this.userPackageId);
        this.weekList = res?.data ?? res ?? [];
      } catch (_) {
        if (!this.weekListInitialized) this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải danh sách tuần.' });
      }
      // Chỉ auto-chọn tuần lần đầu; reload sau khi lưu giữ nguyên tuần đang xem
      if (!this.weekListInitialized) {
        const firstIncomplete = this.weekList.find(w => w.weekNumber > 0 && !w.isCompleted);
        this.selectedWeek = firstIncomplete ? firstIncomplete.weekNumber : 1;
        this.weekListInitialized = true;
      }
      if (this.selectedExternalId || this.selectedPatientId) {
        this.loadPatientInfoFromHis();
      }
    },
    extractPatientCode(raw) {
      if (!raw) return raw;
      const idx = raw.indexOf('_');
      return idx !== -1 ? raw.slice(idx + 1).trim() : raw.trim();
    },
    formatHisDate(value) {
      return value ? this.$moment(value).format('DD/MM/YYYY') : '';
    },
    // Hồ sơ từ patients-by-phone → hiện bệnh viện, ngày sinh, lần khám gần nhất để coach chọn đúng
    toPatientOption(p) {
      const details = [];
      if (p.hospitalName) details.push(p.hospitalName);
      if (p.dateOfBirth) details.push(`NS: ${this.formatHisDate(p.dateOfBirth)}`);
      details.push(p.lastVisitDate ? `Khám gần nhất: ${this.formatHisDate(p.lastVisitDate)}` : 'Chưa có lần khám');
      if (p.externalId) details.push(`Mã: ${p.externalId}`);
      return {
        key: `pid-${p.patientId}`,
        externalId: p.externalId ? String(p.externalId).trim() : null,
        patientId: p.patientId ?? null,
        title: p.fullName?.trim() || `Hồ sơ #${p.patientId}`,
        details,
      };
    },
    // Flow cũ: chỉ có chuỗi "Tên_Mã"
    toLegacyPatientOption(raw) {
      return {
        key: `ext-${raw}`,
        externalId: this.extractPatientCode(raw),
        patientId: null,
        title: raw,
        details: [],
      };
    },
    async fetchPatientOptions() {
      try {
        const res = await getPatientsByPhone(this.phoneNumber);
        return (res?.data ?? []).map(this.toPatientOption);
      } catch (_) {
        // Endpoint mới lỗi / chưa deploy → dùng patient-ids-by-phone
        const res = await getPatientIdsByPhone(this.phoneNumber);
        return (res?.data ?? []).map(this.toLegacyPatientOption);
      }
    },
    async loadExternalId() {
      if (!this.phoneNumber) {
        this.loadCustomerInfoFallback();
        return;
      }
      try {
        const options = await this.fetchPatientOptions();
        if (options.length === 1) {
          this.selectPatientOption(options[0]);
        } else if (options.length > 1) {
          this.patientOptions = options;
          this.pendingPatientKey = options[0].key;
          this.showPatientSelectModal = true;
        } else {
          // Không có hồ sơ HIS → báo cho người dùng và dùng customer-info
          this.$toastr.e({ title: 'Lỗi', msg: 'Khách hàng không có thông tin trong hệ thống HIS' });
          this.loadCustomerInfoFallback();
        }
      } catch (_) {
        this.loadCustomerInfoFallback();
      }
    },
    selectPatientOption(opt) {
      this.selectedExternalId = opt.externalId;
      this.selectedPatientId = opt.patientId;
      this.loadPatientInfoFromHis();
    },
    confirmSelectPatientId() {
      const opt = this.patientOptions.find(o => o.key === this.pendingPatientKey);
      if (!opt) return;
      this.showPatientSelectModal = false;
      this.selectPatientOption(opt);
    },
    cancelSelectPatientId() {
      // Huỷ chọn hồ sơ → vẫn hiện tên/năm sinh từ customer-info
      this.loadCustomerInfoFallback();
    },
    async loadPatientInfoFromHis() {
      if (!this.selectedExternalId && !this.selectedPatientId) return;
      // Chờ weekList để có ngày bắt đầu gói; không có tuần 0 → dùng customer-info
      if (!this.weekListInitialized) return;
      const packageStartDate = this.packageStartDate;
      if (!packageStartDate) {
        this.loadCustomerInfoFallback();
        return;
      }
      const seq = ++this.hisLoadSeq;
      try {
        const hisRes = await getLatestCheckupByPhoneBeforeDate(
          this.selectedExternalId,
          packageStartDate,
          this.selectedPatientId,
        );
        if (seq !== this.hisLoadSeq) return;
        this.hisBaselineData = hisRes;
        const his = hisRes?.data;
        if (!his || !his.piiData) {
          this.loadCustomerInfoFallback();
          return;
        }
        const pii = his.piiData || {};
        // Bác sĩ + chẩn đoán theo lần khám gần nhất trước khi vào gói
        const visit = latestVisit(his);
        const { doctor } = doctorAndNoteOf(prescriptionsOfVisit(his, visit));
        this.patientInfo = {
          ...this.patientInfo,
          name: pii.fullName?.trim() || '',
          phone: this.phoneNumber,
          birthYear: pii.dateOfBirth ? new Date(pii.dateOfBirth).getFullYear() : '',
          address: pii.addressLine1 || '',
          doctor: doctor || this.patientInfo.doctor,
          diagnosis: visit?.dischargeDiagnosis || visit?.admissionDiagnosis || '',
        };
      } catch (_) {
        if (seq !== this.hisLoadSeq) return;
        // HIS 404 / lỗi → header vẫn có tên, năm sinh từ customer-info
        this.patientInfo = { ...this.patientInfo, phone: this.phoneNumber };
        this.loadCustomerInfoFallback();
      }
    },
    onWeekSelect(weekNumber) {
      this.selectedWeek = weekNumber;
      this.activeTab = 0;
    },
    async refreshAll() {
      // Gọi lại toàn bộ API kể từ lúc chọn xong hồ sơ bệnh nhân
      await this.loadPatientInfoFromHis();
      if (this.$refs.tab1) {
        await this.$refs.tab1.loadWeekData();
      }
    },
  },
};
</script>

<style scoped>
.coach-note-page {
  padding: 24px;
  width: 100%;
}
.cn-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}
.cn-main {
  flex: 1;
  min-width: 0;
}
.cn-sidebar {
  width: 220px;
  flex-shrink: 0;
  position: sticky;
  top: 16px;
}
</style>

<style>
.cn-tabs .tab-content {
  min-height: calc(100vh - 220px);
}
</style>
